import { useLockFn, useMemoizedFn, useBoolean } from 'ahooks';
import cn from 'classnames';
import { Loader2 } from 'lucide-react';
import AiManagedIcon from '@/assets/icons/ai-managed-icon.svg?react';
import PinIcon from '@/assets/icons/pin.svg?react';
import ShieldCheck from '@/assets/icons/shield-check.svg?react';
import PinActiveIcon from '@/assets/icons/pin-active.svg?react';
import type { MessageListItemViewModel } from '../index';

interface EmailMessageItemProps {
  data: MessageListItemViewModel;
  unreadCount: number;
  isSelected: boolean;
  onNavigate: (contactId: string) => void;
  onTogglePin?: (contactId: string, isPinned: boolean) => Promise<void>;
  className?: string;
}

// ─── UnreadBadge ──────────────────────────────────────────────────────────────

function UnreadBadge({ count, className }: { count: number; className?: string }) {
  return (
    <div className={cn('flex items-center justify-center w-4 h-4 rounded-[12px] bg-[#EF4444]', className)}>
      <span className="text-white leading-none font-normal" style={{ fontSize: 10, letterSpacing: '1.5%' }}>
        {count > 99 ? '99' : count}
      </span>
    </div>
  );
}

// ─── EmailMessageItem ─────────────────────────────────────────────────────────

export default function EmailMessageItem({
  data,
  unreadCount,
  isSelected,
  onNavigate,
  onTogglePin,
}: EmailMessageItemProps) {
  const isPinned = data.sortType === 'PINNED_TOP';
  const [isPinLoading, { setTrue: startPinLoading, setFalse: stopPinLoading }] = useBoolean(false);

  const handleClick = useMemoizedFn(() => {
    onNavigate(data.id);
  });

  const handlePinClick = useLockFn(async (e: React.MouseEvent) => {
    e.stopPropagation();
    startPinLoading();
    try {
      await onTogglePin?.(data.id, !isPinned);
    } finally {
      stopPinLoading();
    }
  });

  const avatarLetter = data.displayName.charAt(0).toUpperCase();

  return (
    <div
      className={cn(
        'group relative flex items-start gap-2 px-3 py-2 cursor-pointer transition-colors',
        isSelected ? 'bg-white rounded-[8px]' : 'bg-transparent hover:bg-[#F8F8F7] border-b border-[#E5E5E5]'
      )}
      style={
        isSelected
          ? {
              boxShadow: 'rgba(0, 0, 0, 0.08) 0px 6px 11px 2px, rgba(0, 188, 125, 0.1) 0px 0px 0px 0px',
              borderBottom: '1px solid transparent',
            }
          : undefined
      }
      onClick={handleClick}
    >
      {/* 字母头像 */}
      <div className="w-7 h-7 rounded-full bg-[#000000] flex items-center justify-center flex-shrink-0 mt-0.5 relative">
        <span className="text-sm font-medium text-white leading-none">{avatarLetter}</span>
        {unreadCount > 0 && (
          <UnreadBadge count={unreadCount} className="absolute top-1 right-1 translate-x-1/2 -translate-y-1/2" />
        )}
      </div>

      {/* 内容区 */}
      <div className="flex-1 min-w-0 flex flex-col gap-1">
        {/* 第一行：姓名 + shield-check + 时间 + 未读角标 */}
        <div className={cn('flex items-center', isSelected ? 'gap-2' : 'gap-4')}>
          {/* 姓名 + shield-check */}
          <div className="flex items-center gap-1 flex-1 min-w-0">
            <span className="text-sm font-medium truncate" style={{ color: isSelected ? '#101828' : '#262626' }}>
              {data.displayName}
            </span>
            {data.hasShieldCheck && <ShieldCheck />}
          </div>

          {/* 时间 + 未读角标 */}
          <div className="flex items-center gap-1 flex-shrink-0">
            <span className="text-[12px] text-[#A3A3A3]" style={{ letterSpacing: '1.5%' }}>
              {data.timeDisplay}
            </span>
          </div>
        </div>

        {/* 第二行：消息摘要 + pin 图标 */}
        <div className="flex items-center gap-1">
          <p className="text-[12px] text-[#737373] truncate flex-1 min-w-0" style={{ letterSpacing: '1.5%' }}>
            {data.latestPreview}
          </p>

          {/* Pin 图标：loading 时展示 spinner；已置顶时展示 active icon；否则 hover 展示 */}
          <button
            type="button"
            className={cn(
              'flex-shrink-0 flex items-center justify-center w-[18px] h-[18px] text-[#262626] cursor-pointer transition-opacity rounded-full hover:bg-[#E5E5E5]',
              isPinLoading || isPinned ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
            )}
            onClick={handlePinClick}
            disabled={isPinLoading}
            title={isPinned ? 'Unpin' : 'Pin'}
          >
            {isPinLoading ? (
              <Loader2 size={12} className="animate-spin text-[#A3A3A3]" />
            ) : isPinned ? (
              <PinActiveIcon />
            ) : (
              <PinIcon />
            )}
          </button>
        </div>

        {/* 第三行：AI Managed 徽章（可选） */}
        {data.isAiManaged && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#ECFDF5] w-fit">
            <AiManagedIcon width={14} height={14} />
            <span className="text-[12px] font-normal text-[#059669]" style={{ letterSpacing: '1.5%' }}>
              AI Managed
            </span>
          </span>
        )}
      </div>
    </div>
  );
}
